<template>
  <inertia-link :href="itemUrl" class="cursor-pointer" @mouseenter="onItemMouseEnter" @mouseleave="onItemMouseLeave">
    <div class="relative" :class="[hasVideo ? videoWidth : imageWidth, height]">
      <div class="absolute inset-0 rounded-item overflow-hidden group bg-gray-100" :class="showHoverContent ? 'shadow-item' : ''">
        <img v-if="thumb && !hasVideo" :src="thumb" :alt="item.name" loading="lazy" class="w-full h-full object-cover">
        <template v-if="hasVideo">
          <img v-if="gifFile" :src="gifFile" loading="lazy" class="absolute w-full h-full object-cover rounded-item opacity-0 group-hover:opacity-100 z-0 group-hover:z-20 transition-opacity duration-300">
          <video
            v-else
            class="absolute w-full h-full object-cover opacity-0 group-hover:opacity-100 z-0 group-hover:z-20 transition-opacity duration-300 rounded-item"
            @mouseleave="stop($event)"
            @mouseenter="play($event)"
            muted
            loop
          >
            <source :src="videoFile.url" :type="videoFile.mime_type">
            Your browser does not support the video tag.
          </video>
          <img :src="hasVideo ? item.previewImg : item.previews[0]" loading="lazy" class="absolute w-full h-full object-cover z-10 opacity-100 group-hover:z-0 group-hover:opacity-0 transition-opacity duration-200 rounded-item">
        </template>
      </div>
      <div v-show="showHoverContent" class="h-full w-full rounded-item absolute top-0 bg-gradient-to-t from-gray-900 opacity-50 z-40"> </div>
      <div v-show="showHoverContent" class="h-full w-full flex flex-col-reverse absolute top-0 p-2 z-50">
        <div class="w-full flex justify-between">
          <h4 class="text-white font-semibold 2xl:text-lg truncate" :title="item.name">{{ item.name }}</h4>
          <template v-if="item.type !== 'collection' && withFavorite">
            <div v-if="!isFavorite" @click="onFavorite" class="text-white text-opacity-80 hover:text-opacity-100 transition outline-none focus:outline-none">
              <svg v-if="! processingFavorite" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <svg v-if="processingFavorite" class="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <div v-else @click="onFavorite" class="text-white text-opacity-80 hover:text-opacity-100 transition outline-none focus:outline-none">
              <svg v-if="! processingFavorite" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clip-rule="evenodd" />
              </svg>
              <svg v-if="processingFavorite" class="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </template>
        </div>
      </div>
    </div>
  </inertia-link>
</template>
<script>
import axios from 'axios';

export default {
  name: 'ItemComponent',

  props: {
    item: Object,
    withFavorite: {
      type: Boolean,
      default: true
    },
    height: {type: String, default: 'h-48'},
    videoWidth: {type: String, default: 'w-72'},
    imageWidth: {type: String, default: 'w-48'},
  },

  data() {
    return {
      showHoverContent: false,
      isFavorite: false,
      processingFavorite: false,
    }
  },

  computed: {
    gifFile() {
      return this.item.previewGif || null
    },

    videoFile() {
      return this.item.files.find(file => file.mime_type.startsWith('video/'))
    },

    hasVideo() {
      return !! this.videoFile
    },

    itemUrl() {
      if (this.item.type == 'collection') {
        return this.$route('collections.show', this.item.uuid)
      }

      if (this.item.type == 'sermon-kit') {
        return this.$route('sermon-kits.show', this.item.uuid)
      }

      return this.$route('media.show', this.item.id)
    },

    thumb() {
      if (this.item.previewImg) {
        return this.item.previewImg
      }
      
      if (!this.item.previews.length) {
        return
      }

      return this.item.previews[0]
    }
  },

  methods: {
    play(event) {
      event.target.play();
    },

    stop(event) {
      event.target.pause();
    },

    onItemMouseEnter() {
      this.showHoverContent = true;
      if(this.hasVideo) {
        setTimeout(() => {
          this.showHoverContent = false;
        }, 1000);
      }
    },

    onItemMouseLeave() {
      this.showHoverContent = false;
    },

    parseType(type) {
      return type.replace('-', ' ')
    },

    onFavorite($event) {
      $event.preventDefault();

      if (! this.$page.props.user) {
        this.$inertia.visit(this.$route('login.show'))
        return
      }

      this.processingFavorite = true;

      axios.patch(this.$route('media.favorites.toggle', this.item.id), {
        redirect: false,
      }).then(response => {
        this.isFavorite = response.data.favorited
      }).finally(() => this.processingFavorite = false);
    },
  },

  mounted() {
    this.isFavorite = this.item.favorited;
  }
}
</script>
